import { ReactNode, MouseEvent, PropsWithChildren } from 'react';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { BaseTagProps, TagButtonProps, StatusType, TagProps } from './types';
import { useFocusRing } from '@react-aria/focus';
import {
  CounterValue,
  CounterWrapper,
  LabelWrapper,
  TagButtonWrapper,
  TagCloseIcon,
  TagDivWrapper,
} from './atoms';
import { tagOperations } from './tagOperations';

const Counter = ({ children }: { children?: ReactNode }) => (
  <CounterWrapper>
    (<CounterValue>{children}</CounterValue>)
  </CounterWrapper>
);

const BaseTag = (props: PropsWithChildren<BaseTagProps>) => {
  const {
    label,
    count,
    statusType,
    onCloseClick,
    onClick,
    onMouseDown,
    showDragHandle,
    children,
    reactSortableRef,
    dragTargetClass,
    isDisabled,
    style,
    className,
    type = 'button',
    LeftIconComponent,
    onKeyDown,
    ['data-tooltip-text']: tooltipText,
    ['aria-label']: ariaLabel,
    iconName,
    rightIconName,
    leftIcon,
    rightIcon,
    ...rest
  } = props;
  const { isFocusVisible, focusProps } = useFocusRing();

  const handleCloseClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onCloseClick?.();
  };

  const shouldRenderLabel =
    label !== undefined || count !== undefined || children !== undefined;
  const resolvedLeftIcon = tagOperations.getIconProps(
    { iconName, leftIcon },
    'left'
  );
  const resolvedRightIcon = tagOperations.getIconProps(
    { iconName, rightIconName, rightIcon },
    'right'
  );

  const Wrapper: React.ElementType = onClick ? TagButtonWrapper : TagDivWrapper;

  return (
    <Wrapper
      onKeyDown={onKeyDown}
      $statusType={statusType || StatusType.DEFAULT}
      onClick={onClick}
      onMouseDown={onMouseDown}
      {...focusProps}
      tabIndex={onClick ? 0 : -1}
      $isFocused={isFocusVisible}
      $isClickable={Boolean(onClick || onMouseDown)}
      $isClosable={Boolean(onCloseClick)}
      ref={reactSortableRef}
      role={onClick ? 'button' : undefined}
      aria-label={ariaLabel || label}
      type={onClick ? type : undefined}
      $isDisabled={isDisabled}
      style={style}
      className={className}
      {...(tooltipText ? { 'data-tooltip-text': tooltipText } : {})}
      {...rest}
    >
      {showDragHandle && (
        <Icon
          iconName={IconName.DRAG_INDICATOR}
          iconSize={IconSize.SMALL}
          className={dragTargetClass}
        />
      )}
      {resolvedLeftIcon && (
        <Icon
          iconName={resolvedLeftIcon.name}
          iconSize={IconSize.SMALL}
          style={{ color: resolvedLeftIcon?.color }}
        />
      )}
      {LeftIconComponent}
      {shouldRenderLabel && (
        <LabelWrapper className={dragTargetClass}>
          {label}
          {(typeof count === 'number' || count) && <Counter>{count}</Counter>}
          {children}
        </LabelWrapper>
      )}
      {resolvedRightIcon && (
        <Icon
          iconName={resolvedRightIcon.name}
          iconSize={IconSize.SMALL}
          style={{ color: resolvedRightIcon?.color }}
        />
      )}
      {onCloseClick && (
        <TagCloseIcon
          role="button"
          aria-label={label ? `Remove ${label}` : 'Remove'}
          deprecated_onClick={handleCloseClick}
          onMouseDownCapture={evt => evt.stopPropagation()}
        />
      )}
    </Wrapper>
  );
};

export const Tag = (props: PropsWithChildren<TagProps>) => {
  return <BaseTag {...props} />;
};

export const TagButton = (props: PropsWithChildren<TagButtonProps>) => {
  return <BaseTag {...props} />;
};
